import { FC, useEffect, useMemo, useState } from 'react';
import PaginationBar from '../../components/PaginationBar';
import {
  ComplimentaryReason,
  fetchComplimentaryReasons,
} from '../../api/complimentaryReasons';

interface ComplimentaryState {
  records: ComplimentaryReason[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

const ComplimentaryReasonsPanel: FC = () => {
  const [page, setPage] = useState(1);
  const limit = 20;
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [state, setState] = useState<ComplimentaryState>({
    records: [],
    total: 0,
    page: 1,
    limit,
    totalPages: 1,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handle = window.setTimeout(
      () => setDebouncedSearch(search.trim()),
      350,
    );
    return () => window.clearTimeout(handle);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError(null);

    fetchComplimentaryReasons({ page, limit, search: debouncedSearch || undefined })
      .then((response) => {
        if (!isMounted) {
          return;
        }
        setState({
          records: response.data,
          total: response.total,
          page: response.page,
          limit: response.limit,
          totalPages: response.totalPages,
        });
      })
      .catch((err) => {
        if (!isMounted) {
          return;
        }
        setError(
          err instanceof Error ? err.message : 'Failed to load complimentary item reasons.',
        );
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [page, limit, debouncedSearch]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(state.total / limit || 1)),
    [state.total, limit],
  );

  return (
    <div className="rounded-2xl bg-white px-6 py-5 shadow-inner">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search reasons..."
          className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-600 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 sm:w-72"
        />
      </div>

      <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200">
        <table className="min-w-full text-left text-sm text-slate-600">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-5 py-3 font-semibold">ERP ID</th>
              <th className="px-5 py-3 font-semibold">Reason</th>
              <th className="px-5 py-3 font-semibold">Updated</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td className="px-5 py-6 text-center text-slate-400" colSpan={3}>
                  Loading reasons...
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td className="px-5 py-6 text-center text-danger" colSpan={3}>
                  {error}
                </td>
              </tr>
            ) : state.records.length === 0 ? (
              <tr>
                <td className="px-5 py-6 text-center text-slate-400" colSpan={3}>
                  No complimentary item reasons found.
                </td>
              </tr>
            ) : (
              state.records.map((reason) => (
                <tr key={reason.id} className="even:bg-slate-50/60">
                  <td className="px-5 py-3 font-medium text-slate-700">{reason.erpId}</td>
                  <td className="px-5 py-3 text-slate-500">{reason.reason}</td>
                  <td className="px-5 py-3 text-slate-500">
                    {new Date(reason.updatedAt).toLocaleString()}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <PaginationBar
        page={state.page}
        totalPages={totalPages}
        onPrev={() => setPage((prev) => Math.max(1, prev - 1))}
        onNext={() => setPage((prev) => Math.min(totalPages, prev + 1))}
        onSelect={(num) => setPage(num)}
        disabled={loading}
        renderSummary={
          <span>
            Showing {(state.page - 1) * limit + Math.min(limit, state.records.length)} of {state.total}{' '}
            reasons
          </span>
        }
        renderExtra={
          <span className="text-xs text-slate-400">Page {state.page} of {totalPages} (20 / page)</span>
        }
      />
    </div>
  );
};

export default ComplimentaryReasonsPanel;
